<template>
  <div class="home-container">
    <el-row :gutter="20" class="home-row">
      <!-- 左侧：待办事项（内部 7 项单独权限） -->
      <el-col v-if="showTodoModule" :span="12" class="home-left-col">
        <el-card class="box-card summary-card" shadow="hover" v-loading="loading.summary">
          <div slot="header" class="card-header">
            <span class="card-title">
              <i class="el-icon-bell"></i> 待办事项
            </span>
          </div>
          <el-row :gutter="12" class="kpi-row">
            <el-col :span="8" v-for="(item, index) in visibleSummaryList" :key="'summary-' + item.key">
              <div
                class="box-div is-clickable"
                :class="'card' + ((index % 5) + 1)"
                @click="openSummaryPage(item)"
              >
                <div class="webBox">
                  <div class="webO">{{ item.label }}:</div>
                  <div class="webT">{{ item.value }}</div>
                </div>
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>

      <!-- 右侧：用户信息和数据汇总 -->
      <el-col :span="showTodoModule ? 12 : 24" class="home-right-col">
        <el-card class="box-card user-info-card" shadow="hover">
          <div slot="header" class="card-header">
            <span class="card-title">
              <i class="el-icon-user"></i> 账号信息
            </span>
          </div>
          <div class="user-info">
            <div class="user-info-item">
              <span class="info-label">用户名称：</span>
              <span class="info-value">{{ userInfo.userName }}</span>
            </div>
            <div class="user-info-item">
              <span class="info-label">登录账号：</span>
              <span class="info-value">{{ userInfo.userAccount }}</span>
            </div>
            <div class="user-info-item">
              <span class="info-label">所属公司：</span>
              <span class="info-value">{{ userInfo.loginTenantName }}</span>
            </div>
          </div>
        </el-card>

        <el-card v-if="showStatisticsModule" class="box-card statistics-card" shadow="hover" v-loading="loading.statistics">
          <div slot="header" class="card-header">
            <span class="card-title">
              <i class="el-icon-s-order"></i> 数据汇总
            </span>
          </div>
          <el-row :gutter="12" class="kpi-row">
            <el-col :span="8" v-for="(item, index) in statisticsList" :key="'stat-' + index">
              <div class="box-div" :class="'card' + ((index % 5) + 1)">
                <div class="webBox">
                  <div class="webO">{{ item.label }}:</div>
                  <div class="webT">{{ item.value }}</div>
                </div>
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { getDataSummary, getStatistics } from '@/api/contract/contract'
import {
  getButtonData,
  hasPermissionMethod,
  parsePermissionButtons,
  findMenuByPath
} from '@/utiljs/permissionButton'

/**
 * 权限配置（权限管理 → 首页方法），建议激活状态填 toolbar：
 * 未下载 → showUnconfirmed
 * 待分派 → showUndispatched
 * 待录入 → showUninspected
 * 待整改 → showUnrectified
 * 待提交 → showUnsubmitted
 * 待审批 → showUnaudited
 * 待签批 → showUnissued
 * 数据汇总整块 → showStatisticsModule
 *
 * 兼容：若只配了旧的 showTodoModule、没有任何单项权限，则 7 项全显示。
 * 一旦配了任意单项（如 showUndispatched），则严格按单项过滤，showTodoModule 不再整包放开。
 */
const STATISTICS_KEYS = ['showStatisticsModule', '数据汇总']
const LEGACY_TODO_KEYS = ['showTodoModule', '待办事项']

const SUMMARY_ITEM_KEYS = [
  'showUnconfirmed', '未下载',
  'showUndispatched', '待分派', '未分派',
  'showUninspected', '待录入',
  'showUnrectified', '待整改',
  'showUnsubmitted', '待提交',
  'showUnaudited', '待审批', '待审核',
  'showUnissued', '待签批', '待签发'
]

const SUMMARY_ITEMS = [
  {
    key: 'unconfirmed',
    label: '未下载',
    field: 'unconfirmedCount',
    permissionKeys: ['showUnconfirmed', '未下载'],
    path: 'views/business/informationManagement/index.vue',
    title: '数据信息管理'
  },
  {
    key: 'undispatched',
    label: '待分派',
    field: 'undispatchedCount',
    permissionKeys: ['showUndispatched', '待分派', '未分派'],
    path: 'views/business/Dispatch/index.vue',
    title: '派单管理'
  },
  {
    key: 'uninspected',
    label: '待录入',
    field: 'uninspectedCount',
    permissionKeys: ['showUninspected', '待录入'],
    path: 'views/business/OriginalManagement/index.vue',
    title: '数据录入'
  },
  {
    key: 'unrectified',
    label: '待整改',
    field: 'unrectifiedCount',
    permissionKeys: ['showUnrectified', '待整改'],
    path: 'views/business/rectificationItem/index.vue',
    title: '整改项'
  },
  {
    key: 'unsubmitted',
    label: '待提交',
    field: 'unsubmittedCount',
    permissionKeys: ['showUnsubmitted', '待提交'],
    path: 'views/business/PendingReview/index.vue',
    title: '待提交报告'
  },
  {
    key: 'unaudited',
    label: '待审批',
    field: 'unauditedCount',
    permissionKeys: ['showUnaudited', '待审批', '待审核'],
    path: 'views/business/ReportReview/index.vue',
    title: '报告审批'
  },
  {
    key: 'unissued',
    label: '待签批',
    field: 'unissuedCount',
    permissionKeys: ['showUnissued', '待签批', '待签发'],
    path: 'views/business/ReportIssuance/index.vue',
    title: '报告签批'
  }
]

export default {
  props: {
    url: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      loading: {
        summary: false,
        statistics: false
      },
      summaryData: {
        unconfirmedCount: 0,
        undispatchedCount: 0,
        uninspectedCount: 0,
        unrectifiedCount: 0,
        unsubmittedCount: 0,
        unauditedCount: 0,
        unissuedCount: 0
      },
      statisticsData: {
        annualReceivableAmount: 0,
        annualReceiptAmount: 0,
        monthlyInspectionCount: 0,
        monthlyAuditCount: 0,
        monthlyApprovalCount: 0
      }
    }
  },
  computed: {
    userInfo () {
      return this.$store.state.loginUser
    },
    permissionButtons () {
      return parsePermissionButtons(this.url)
    },
    allTodoPermissionKeys () {
      const keys = [].concat(LEGACY_TODO_KEYS)
      SUMMARY_ITEMS.forEach(item => {
        item.permissionKeys.forEach(k => keys.push(k))
      })
      return keys
    },
    homePermissionReady () {
      if (!(this.url && String(this.url).indexOf('Home.vue') !== -1)) return false
      const bd = getButtonData(this.url)
      if (!bd) return false
      return this.allTodoPermissionKeys.concat(STATISTICS_KEYS).some(key => bd.indexOf(key) !== -1)
    },
    hasLegacyTodoPermission () {
      return this.hasHomePermission(LEGACY_TODO_KEYS)
    },
    // buttonData 里是否已出现任意「单项」权限
    hasItemPermissionConfigured () {
      const bd = getButtonData(this.url)
      if (!bd) return false
      return SUMMARY_ITEM_KEYS.some(key => bd.indexOf(key) !== -1)
    },
    summaryList () {
      const d = this.summaryData
      return SUMMARY_ITEMS.map(item => ({
        ...item,
        value: d[item.field]
      }))
    },
    visibleSummaryList () {
      if (!this.homePermissionReady) {
        return this.summaryList
      }
      // 已配置单项权限：严格按单项显示（忽略旧的 showTodoModule 整包）
      if (this.hasItemPermissionConfigured) {
        return this.summaryList.filter(item => this.hasHomePermission(item.permissionKeys))
      }
      // 仅有旧整模块权限时，才显示全部
      if (this.hasLegacyTodoPermission) {
        return this.summaryList
      }
      return []
    },
    showTodoModule () {
      return this.visibleSummaryList.length > 0
    },
    showStatisticsModule () {
      if (!this.homePermissionReady) return true
      return this.hasHomePermission(STATISTICS_KEYS)
    },
    statisticsList () {
      const d = this.statisticsData
      return [
        { label: '年度应收款', value: '￥' + this.formatAmount(d.annualReceivableAmount) },
        { label: '年度已收款', value: '￥' + this.formatAmount(d.annualReceiptAmount) },
        { label: '当月检测量', value: d.monthlyInspectionCount },
        { label: '当月审核量', value: d.monthlyAuditCount },
        { label: '当月签发量', value: d.monthlyApprovalCount }
      ]
    }
  },
  watch: {
    url () {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    hasHomePermission (keys) {
      return keys.some(key => {
        if (hasPermissionMethod(this.url, key)) return true
        return this.permissionButtons.some(btn => btn.value === key || btn.onclick === key)
      })
    },
    loadData () {
      if (this.showTodoModule) {
        this.fetchTodoCardData()
      }
      if (this.showStatisticsModule) {
        this.fetchStatisticsCardData()
      }
    },
    formatAmount (val) {
      const num = Number(val)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    fetchTodoCardData () {
      this.loading.summary = true
      getStatistics().then((res) => {
        if (res.additionalData) {
          this.summaryData = res.additionalData
        }
      }).catch((error) => {
        console.error('获取待办事项失败：', error)
        this.$message.error(error.message || '获取待办事项失败')
      }).finally(() => {
        this.loading.summary = false
      })
    },
    fetchStatisticsCardData () {
      this.loading.statistics = true
      getDataSummary().then((res) => {
        if (res.additionalData) {
          this.statisticsData = res.additionalData
        }
      }).catch((error) => {
        console.error('获取数据汇总失败：', error)
        this.$message.error(error.message || '获取数据汇总失败')
      }).finally(() => {
        this.loading.statistics = false
      })
    },
    findMainParent () {
      let parent = this.$parent
      while (parent && !parent.addTab) {
        parent = parent.$parent
      }
      return parent
    },
    openSummaryPage (item) {
      if (!item || !item.path) return
      const main = this.findMainParent()
      if (!main || !main.addTab) {
        this.$message.warning('无法打开页面，请从左侧菜单进入')
        return
      }
      const groups = [].concat(main.bussinessData || [], main.systemData || [])
      const menu = findMenuByPath(groups, item.path)
      const tabTitle = (menu && menu.title) || item.title
      const tabUrl = (menu && menu.url) || item.path
      main.addTab(tabTitle, tabUrl)
    }
  }
}
</script>

<style scoped>
.home-container {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: 100vh;
}

.home-row {
  display: flex;
  height: 100%;
}

.box-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.card-title i {
  margin-right: 8px;
  color: #409eff;
}

.summary-card {
  height: calc(100vh - 80px);
}

.summary-card >>> .el-card__body {
  height: calc(100vh - 180px);
  overflow-y: auto;
}

.user-info-card {
  margin-bottom: 20px;
}

.user-info {
  padding: 10px 0;
}

.user-info-item {
  display: flex;
  align-items: center;
  padding: 12px 10px;
  border-bottom: 1px solid #ebeef5;
}

.user-info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: bold;
  color: #606266;
  min-width: 100px;
  font-size: 14px;
}

.info-value {
  color: #303133;
  font-size: 14px;
}

.statistics-card {
  margin-bottom: 0;
}

.statistics-card >>> .el-card__body {
  overflow-y: auto;
}

.kpi-row {
  display: flex;
  flex-wrap: wrap;
  height: auto !important;
  align-items: flex-start;
}

.kpi-row >>> .el-col {
  height: auto;
  align-self: flex-start;
  margin-bottom: 12px;
}

.box-div {
  margin-bottom: 0;
  border-radius: 8px;
  padding: 16px 14px;
  color: #fff;
  min-height: 88px;
  box-sizing: border-box;
}

.box-div.is-clickable {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.box-div.is-clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.webBox {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

.webO {
  font-size: 13px;
  opacity: 0.95;
  margin-bottom: 8px;
}

.webT {
  font-size: 22px;
  font-weight: bold;
  word-break: break-all;
  line-height: 1.2;
}

.card1 {
  background: linear-gradient(135deg, #409eff, #66b1ff);
}

.card2 {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.card3 {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.card4 {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.card5 {
  background: linear-gradient(135deg, #909399, #a6a9ad);
}
</style>
